import { useEffect, useRef } from "react";
import { Button } from "@cntrl/ui";
import styles from "./ConfirmDialog.module.scss";

// FE-SH-02: a hand-rolled destructive-confirm dialog on the ProfileModal chrome precedent (backdrop + scrim +
// role="alertdialog"/aria-modal + focus-trap + Escape + focus-restore). The design-system `AcDialog`
// (destructive variant) is not stood up in the current @cntrl/ui subset; promote this to a Storybook'd
// primitive when the shared branding layer lands. Used by the disconnect-team + revoke-invite double-confirms.

export interface ConfirmDialogProps {
  title: string;
  /** The explanation body — for a disconnect this states the cascade ("Current sharing stops. Past data stays."). */
  description: React.ReactNode;
  /** The destructive action label (e.g. "Remove team"). */
  confirmLabel: string;
  /** Verbatim server error from a failed attempt (the dialog stays open and shows it). */
  error?: string | null;
  loading?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/** A focus-trapped destructive-confirm modal. The confirm button is `accent` (crimson) — the ONE band-tool
 *  place crimson is a button, matching the danger treatment.*/
export function ConfirmDialog({
  title,
  description,
  confirmLabel,
  error,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  useEffect(() => {
    const el = modalRef.current;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || !el) return;
      const focusables = el.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    el?.addEventListener("keydown", onKey);
    return () => {
      el?.removeEventListener("keydown", onKey);
      previouslyFocused?.focus?.();
    };
  }, []);

  return (
    <div className={styles.backdrop}>
      <button type="button" className={styles.scrim} aria-label="Cancel" tabIndex={-1} onClick={onCancel} />
      <div
        ref={modalRef}
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-desc"
      >
        <h2 id="confirm-title" className={styles.title}>
          {title}
        </h2>
        <div id="confirm-desc" className={styles.desc}>
          {description}
        </div>
        {error && (
          <p className={styles.error} role="alert">
            {error}
          </p>
        )}
        <div className={styles.actions}>
          <Button variant="ghost" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button ref={confirmRef} variant="accent" onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
